import { CommercialTerms } from "./CommercialTerms";
import {
  standardCommercialGoverningTerms,
  type CommercialDocumentInput,
} from "../../shared/commercial-documents";
import { Button } from "../components/ui/button";
import { Field, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

type Props = { value: CommercialDocumentInput; onChange: (value: CommercialDocumentInput) => void };
const details = [
  ["title", "Document title", 200],
  ["customerName", "Customer", 160],
  ["reference", "Reference number", 80],
  ["toolName", "Tool / project", 200],
  ["contactName", "Contact name", 160],
  ["contactEmail", "Contact email", 254],
  ["initialTerm", "Initial term", 300],
] as const;
export function CommercialDocumentFields({ value, onChange }: Props) {
  return (
    <div className="space-y-8">
      <section aria-label="Document details" className="space-y-4 border-b pb-6">
        <h2 className="text-lg font-semibold">Document details</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {details.map(([key, label, max]) => (
            <Field key={key}>
              <FieldLabel htmlFor={`commercial-${key}`}>{label}</FieldLabel>
              <Input
                id={`commercial-${key}`}
                maxLength={max}
                value={value[key]}
                type={key === "contactEmail" ? "email" : "text"}
                onChange={(event) => {
                  onChange({ ...value, [key]: event.target.value });
                }}
              />
            </Field>
          ))}
          <Field>
            <FieldLabel htmlFor="commercial-date">Document date</FieldLabel>
            <Input
              id="commercial-date"
              type="date"
              value={value.documentDate}
              onChange={(event) => {
                onChange({ ...value, documentDate: event.target.value });
              }}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="commercial-expiry">Expiration date (optional)</FieldLabel>
            <Input
              id="commercial-expiry"
              type="date"
              value={value.expiresOn ?? ""}
              onChange={(event) => {
                onChange({ ...value, expiresOn: event.target.value || null });
              }}
            />
          </Field>
          {value.kind === "change_order" ? (
            <Field>
              <FieldLabel htmlFor="commercial-original">Original order reference</FieldLabel>
              <Input
                id="commercial-original"
                value={value.originalReference}
                maxLength={200}
                onChange={(event) => {
                  onChange({ ...value, originalReference: event.target.value });
                }}
              />
            </Field>
          ) : null}
        </div>
      </section>
      <CommercialTerms value={value} onChange={onChange} />
      <DocumentSections value={value} onChange={onChange} />
      <label className="flex min-h-10 items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={value.includeAcceptance}
          onChange={(event) => {
            onChange({ ...value, includeAcceptance: event.target.checked });
          }}
        />
        Include customer signature lines
      </label>
    </div>
  );
}
function DocumentSections({ value, onChange }: Props) {
  const sections = [
    ["scope", value.kind === "change_order" ? "Changes to scope" : "Scope", 20000],
    ["scopeControls", "Scope controls (optional)", 4000],
    ["timelineImpact", "Timeline impact (optional)", 4000],
    ["governingTerms", "Governing terms (optional)", 8000],
  ] as const;
  return (
    <section aria-label="Scope and conditions" className="space-y-5 border-t pt-6">
      <h2 className="text-lg font-semibold">Scope & conditions</h2>
      {sections.map(([key, label, max]) => (
        <Field key={key}>
          <FieldLabel htmlFor={`commercial-${key}`}>{label}</FieldLabel>
          <Textarea
            id={`commercial-${key}`}
            rows={key === "scope" ? 8 : 4}
            maxLength={max}
            value={value[key]}
            onChange={(event) => {
              onChange({ ...value, [key]: event.target.value });
            }}
          />
          {key === "governingTerms" ? (
            <Button
              type="button"
              variant="link"
              className="h-auto justify-start p-0"
              onClick={() => {
                onChange({
                  ...value,
                  governingTerms: standardCommercialGoverningTerms(value.kind),
                });
              }}
            >
              Use standard terms
            </Button>
          ) : null}
        </Field>
      ))}
    </section>
  );
}
